import { FC, useMemo, ReactNode } from 'react';

import { getISOWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { PlannedHour, Baseplan } from 'components/types';

import { getWeekCycles } from '../utils';
import { formatByLocale, formatISOString } from 'utils/date';

import Table from 'components/shared/Table/Table';

interface PlannedHoursTableProps {
  children?: ReactNode | ReactNode[];
  plannedHours: PlannedHour[];
  onAddClick: () => void;
  onEditClick: (startDate: string) => void;
  onDeleteClick: (startDate: string) => void;
  baseplans: Baseplan[];
  year: number;
  disableActions?: boolean;
}

const PlannedHoursTable: FC<PlannedHoursTableProps> = ({
  children,
  plannedHours,
  baseplans,
  year,
  onAddClick,
  onEditClick,
  onDeleteClick,
  disableActions
}) => {
  const { t } = useTranslation('common');

  const data = useMemo(
    () => ({
      columns: [
        t('planUnit'),
        t('baseplan.plannedHoursTable.name'),
        t('baseplan.plannedHoursTable.startDate'),
        t('baseplan.plannedHoursTable.startWeek'),
        t('baseplan.plannedHoursTable.endDate'),
        t('baseplan.plannedHoursTable.weeksInCycle')
      ],
      rows: plannedHours.map(
        ({
          organizationalUnit,
          baseplanId: _baseplanId,
          startDate,
          endDate
        }) => {
          const name = baseplans?.find(
            ({ baseplanId }) => baseplanId === _baseplanId
          )?.name;

          const baseplan = baseplans.find(
            ({ baseplanId }) => baseplanId === _baseplanId
          );

          return {
            id: startDate,
            cells: [
              organizationalUnit,
              name as string,
              formatISOString(startDate),
              getISOWeek(new Date(startDate)),
              formatByLocale(new Date(endDate)),
              baseplan ? getWeekCycles(baseplan, startDate) : 0
            ]
          };
        }
      )
    }),
    [year, plannedHours, baseplans]
  );

  return (
    <Table
      disableActions={disableActions}
      data={data}
      onAddClick={onAddClick}
      onEditClick={(id) => onEditClick(id as string)}
      onDeleteClick={(id) => onDeleteClick(id as string)}
      addRowDialog={children}
    />
  );
};

export default PlannedHoursTable;
